import { Result } from 'domain/types';
import { right, left } from 'fp-ts/lib/Either';
import { toSnakeCase } from 'js-convert-case';
import { JsonSerializer, throwError, isNullish } from 'typescript-json-serializer';

import { Type } from 'typescript-json-serializer/dist/helpers';

export interface Typed<T extends string> {
    type: T;
}

type Nullish = null | undefined;

const customSerializer = new JsonSerializer({
    errorCallback: throwError,

    // Allow all nullish values
    nullishPolicy: {
        undefined: 'remove',
        null: 'remove',
    },

    // Disallow additional properties (non JsonProperty)
    // additionalPropertiesPolicy: 'remove',

    formatPropertyName: (propertyName: string) => toSnakeCase(propertyName),
});

const deserialize = <T extends object>(classType: Type<T> | T) => (
    responseJson: Record<string, unknown> | Array<Record<string, unknown>>
): Result<T> => {
    try {
        const result = customSerializer.deserializeObject(responseJson, classType);
        if (result) {
            return right(result);
        }
        throw new Error();
    } catch (error) {
        return left({ error: new Error(`Error serializing ${classType.constructor.name} ${error}`), status: 500 });
    }
};

const deserializeArray = <T extends object>(classType: Type<T> | T) => (
    responseJson: Record<string, unknown> | Array<Record<string, unknown>>
): Result<T[]> => {
    const noNullishArePresent = (result: T | Nullish | (T | Nullish)[]): result is T[] =>
        !isNullish(result) && Array.isArray(result) && !result.some((value) => isNullish(value));

    try {
        const result = customSerializer.deserialize(responseJson, classType);
        if (result && noNullishArePresent(result)) {
            return right(result);
        }
        throw new Error();
    } catch (error) {
        return left({ error: new Error(`Error serializing ${classType.constructor.name} ${error}`), status: 500 });
    }
};

export const deserializeResponse = <T extends object, P extends object, Q>(
    httpClientResponse: Promise<Response>,
    classType: Type<T> | T,
    mapper?: (json: P) => Q
): Promise<Result<T>> =>
    httpClientResponse
        .then((response) =>
            response.ok
                ? response
                      .json()
                      .then((json) => (mapper ? mapper(json) : json))
                      .then(deserialize(classType))
                : left({ error: new Error(), status: response.status })
        )
        .catch(() => left({ error: new Error(), status: 503 }));

export const deserializeArrayResponse = <T extends object, P extends object, Q>(
    httpClientResponse: Promise<Response>,
    classType: Type<T> | T,
    mapper?: (json: P) => Q
): Promise<Result<T[]>> =>
    httpClientResponse
        .then((response) =>
            response.ok
                ? response
                      .json()
                      .then((json) => (mapper ? mapper(json) : json))
                      .then(deserializeArray(classType))
                : left({ error: new Error(), status: response.status })
        )
        .catch(() => left({ error: new Error(), status: 503 }));

export const getTypes = <T extends string>(map: Map<T, unknown>) => (json: Typed<T>) => map.get(json.type);
